import { useCallback, useEffect, useState } from "react";

export type WeightUnit = "kg" | "lbs";

export const LBS_PER_KG = 2.20462262185;
export const KG_PER_LBS = 0.45359237;

const STORAGE_KEY = "thecoach_weight_unit";
const UNIT_CHANGE_EVENT = "thecoach_weight_unit_change";

/**
 * Converts a weight from KG to the target unit (KG or LBS).
 * If target is LBS and roundStep is specified, rounds to that step (e.g. 2.5 or 5).
 * Otherwise rounds cleanly to 1 decimal place (or integer if .0).
 */
export function kgToUnit(
  kg: number | null | undefined,
  unit: WeightUnit,
  roundStep?: number,
): number | null {
  if (kg === null || kg === undefined || isNaN(kg)) return null;
  if (unit === "kg") return kg;

  const rawLbs = kg * LBS_PER_KG;
  if (roundStep && roundStep > 0) {
    return Math.round(rawLbs / roundStep) * roundStep;
  }

  // Round to 1 decimal place
  return Math.round(rawLbs * 10) / 10;
}

/**
 * Converts a weight entered in the given unit back to standard KG for database storage.
 */
export function unitToKg(
  value: number | null | undefined,
  unit: WeightUnit,
): number | null {
  if (value === null || value === undefined || isNaN(value)) return null;
  if (unit === "kg") return value;

  const rawKg = value * KG_PER_LBS;
  // Round to 2 decimal places to avoid floating point weirdness (e.g. 102.0582...)
  return Math.round(rawKg * 100) / 100;
}

/**
 * Formats a weight with unit for display (e.g. "100 kg" or "225 lbs")
 */
export function formatWeightWithUnit(
  weight: number | null | undefined,
  unit: WeightUnit,
  decimals = 1,
): string {
  if (weight === null || weight === undefined || isNaN(weight)) return "-";
  const num = Number(weight.toFixed(decimals));
  return `${num} ${unit}`;
}

/**
 * React hook to manage and synchronize weight unit (kg | lbs) across components and tabs.
 */
export function useWeightUnit() {
  const [unit, setUnitState] = useState<WeightUnit>(() => {
    if (typeof window === "undefined") return "kg";
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "lbs" ? "lbs" : "kg";
    } catch {
      return "kg";
    }
  });

  const setUnit = useCallback((newUnit: WeightUnit) => {
    setUnitState(newUnit);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, newUnit);
        window.dispatchEvent(
          new CustomEvent(UNIT_CHANGE_EVENT, { detail: newUnit }),
        );
      } catch (err) {
        console.error("Failed to save weight unit to localStorage", err);
      }
    }
  }, []);

  const toggleUnit = useCallback(() => {
    setUnit(unit === "kg" ? "lbs" : "kg");
  }, [unit, setUnit]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<WeightUnit>;
      if (
        customEvent.detail &&
        (customEvent.detail === "kg" || customEvent.detail === "lbs")
      ) {
        setUnitState(customEvent.detail);
      }
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (
        e.key === STORAGE_KEY &&
        (e.newValue === "kg" || e.newValue === "lbs")
      ) {
        setUnitState(e.newValue);
      }
    };

    window.addEventListener(UNIT_CHANGE_EVENT, handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener(UNIT_CHANGE_EVENT, handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  const convertFromKg = useCallback(
    (kg: number | null | undefined, roundStep?: number) =>
      kgToUnit(kg, unit, roundStep),
    [unit],
  );

  const convertToKg = useCallback(
    (value: number | null | undefined) => unitToKg(value, unit),
    [unit],
  );

  return {
    unit,
    setUnit,
    toggleUnit,
    convertFromKg,
    convertToKg,
  };
}
